import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { QrCode, ArrowRight, Star, Loader, AlertTriangle } from 'lucide-react';

interface Question {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'checkbox' | 'radio' | 'select' | 'rating';
  required: boolean;
  options?: string[];
}

interface Form {
  id: string;
  name: string;
  description?: string;
  questions: Question[];
  is_active: boolean;
  user_id: string;
  notification_email?: string;
  email_notifications_enabled?: boolean;
  thank_you_message?: string;
  profiles?: {
    company_name?: string;
    company_logo?: string;
    notification_email?: string;
  };
}

interface FormResponse {
  question_id: string;
  question_text: string;
  question_type: string;
  response: string;
}

interface RatingInputProps {
  questionId: string;
  value: string;
  onChange: (questionId: string, value: string) => void;
}

const FeedbackFormPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');

  useEffect(() => {
    fetchForm();
  }, [formId]);

  const fetchForm = async () => {
    try {
      setLoading(true);
      setFormError('');// First fetch the form data
      const { data: formData, error: formError } = await supabase
        .from('forms')
        .select('*')
        .eq('id', formId)
        .single();

      if (formError) {if (formError.code === 'PGRST116') {
          setFormError('Form not found or no longer available.');
        } else {
          setFormError('An error occurred while loading the form.');
        }
        setForm(null);
        return;
      }

      if (!formData) {setFormError('Form not found or no longer available.');
        setForm(null);
        return;
      }// Check if form is accessible based on subscription status
      const { data: accessibilityResult, error: accessError } = await supabase
        .rpc('is_form_accessible', { form_id_param: formId });

      if (accessError) {setFormError('An error occurred while loading the form.');
        setForm(null);
        return;
      }

      if (!accessibilityResult) {
        setFormError('This form is currently unavailable because the owner\'s trial period has expired. The form will be reactivated once the owner subscribes to a plan. Please try again later or contact the form owner.');
        setForm(null);
        return;
      }

      // Check if form is active (this is the manual toggle by the user)
      if (!formData.is_active) {
        setFormError('This form is no longer active.');
        setForm(null);
        return;
      }

      // Then fetch the profile data separately
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('company_name, company_logo, notification_email')
        .eq('id', formData.user_id)
        .maybeSingle();

      if (profileError) {} else if (profileData) {
        // Merge profile data with form data
        formData.profiles = profileData;
      } else {
        formData.profiles = null;
      }

      // Set form data
      setForm(formData as Form);

      // Handle logo URL
      const logoUrl = profileData?.company_logo;
      setCompanyLogo(logoUrl || '');

      // Initialize responses
      const initialResponses: Record<string, string | string[]> = {};
      if (formData.questions && Array.isArray(formData.questions)) {
        formData.questions.forEach((question: Question) => {
          initialResponses[question.id] = question.type === 'checkbox' ? [] : '';
        });
      }
      setResponses(initialResponses);

    } catch (error) {setFormError('An unexpected error occurred while loading the form.');
      setForm(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (questionId: string, value: string, checked: boolean) => {
    setResponses(prev => {
      const currentValues = [...(prev[questionId] || [])];
      
      if (checked) {
        return {
          ...prev,
          [questionId]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [questionId]: currentValues.filter(v => v !== value)
        };
      }
    });
    
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    if (form?.questions && Array.isArray(form.questions)) {
      form.questions.forEach(question => {
        if (question.required) {
          const response = responses[question.id];
          
          if (
            response === undefined || 
            response === null || 
            response === '' || 
            (Array.isArray(response) && response.length === 0)
          ) {
            newErrors[question.id] = 'This question is required';
            isValid = false;
          }
        }
      });
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!form || !validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    // Debug information for mobile issues// Test database connectivity
    try {
      const { data: testData, error: testError } = await supabase
        .from('forms')
        .select('id, name, is_active')
        .eq('id', formId)
        .single();} catch (connectError) {}
    
    try {
      const formattedResponses: FormResponse[] = Object.entries(responses).map(([questionId, response]) => {
        const question = form.questions.find(q => q.id === questionId);
        return {
          question_id: questionId,
          question_text: question?.label || '',
          question_type: question?.type || '',
          response: Array.isArray(response) ? response.join(', ') : String(response)
        };
      });
      
      // Log the data being inserted for debugging
      const insertData = {
        form_id: formId,
        user_id: null, // Set to null for public submissions
        responses: formattedResponses
      };// Insert the response and get the response ID
      const { data: responseData, error } = await supabase
        .from('responses')
        .insert([insertData])
        .select('id')
        .single();
      
      if (error) throw error;
      
      // Send email notification if enabled and email is configured
      const notificationEmail = form.notification_email || form.profiles?.notification_email;
      if (form.email_notifications_enabled && notificationEmail && responseData?.id) {
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              response_id: responseData.id,
              form_id: form.id,
              form_name: form.name,
              recipient_email: notificationEmail,
              company_name: form.profiles?.company_name || null,
              responses: formattedResponses
            }
          });
          
          // Email sent successfully (no error thrown)
        } catch (emailError) {
          // Log email error but don't fail the form submission
          console.warn('Email notification error:', emailError);
        }
      }
      
      navigate('/thank-you', { 
        state: { 
          message: form.thank_you_message,
          formName: form.name
        } 
      });
    } catch (error) {const dbError = error as any;// More specific error messages based on the error type
      if (dbError?.code === 'PGRST301') {
        setFormError('Database connection error. Please check your internet connection and try again.');
      } else if (dbError?.code === '42501') {
        setFormError('Permission denied. This form may not be configured for public submissions.');
      } else if (dbError?.message?.includes('violates not-null constraint')) {
        setFormError('Database configuration error. Please contact the form owner.');
      } else {
        setFormError('Failed to submit your feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const RatingInput: React.FC<RatingInputProps> = ({ questionId, value, onChange }) => {
    const stars = [1, 2, 3, 4, 5];
    
    return (
      <div className="flex items-center space-x-1">
        {stars.map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(questionId, star.toString())}
            className="focus:outline-none"
          >
            <Star 
              size={32} 
              className={`${
                parseInt(value) >= star 
                  ? 'text-yellow-400 fill-yellow-400' 
                  : 'text-gray-300'
              }`} 
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-500">
          {value ? `${value} of 5` : 'Click to rate'}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <Loader size={40} className="animate-spin text-blue-500" />
        <p className="mt-4 text-gray-600">Loading feedback form...</p>
      </div>
    );
  }

  if (!form || formError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <AlertTriangle size={48} className="text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Form Unavailable</h1>
          <p className="text-gray-600 mb-6">{formError || 'This form is not available.'}</p>
          <a 
            href="/"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full mx-auto">
        <div className="text-center mb-8">
          {companyLogo ? (
            <img 
              src={companyLogo} 
              alt={`${form?.profiles?.company_name || 'Company'} Logo`}
              className="h-24 mx-auto mb-6 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                setCompanyLogo('');
              }}
            />
          ) : (
            <div className="flex justify-center mb-6">
              <QrCode size={40} className="text-blue-600" />
            </div>
          )}
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {form.name}
          </h2>
          {form.description && (
            <p className="mt-2 text-sm text-gray-600">
              {form.description}
            </p>
          )}
        </div>

        {formError && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{formError}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {form.questions && Array.isArray(form.questions) && form.questions.map((question, index) => (
              <div key={question.id} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {question.label}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {question.type === 'text' && (
                  <input
                    type="text"
                    value={responses[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    className={`block w-full shadow-sm sm:text-sm rounded-md ${
                      errors[question.id] 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                )}
                
                {question.type === 'textarea' && (
                  <textarea
                    rows={3}
                    value={responses[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    className={`block w-full shadow-sm sm:text-sm rounded-md ${
                      errors[question.id] 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                )}
                
                {question.type === 'radio' && question.options && Array.isArray(question.options) && (
                  <div className="space-y-2">
                    {question.options.map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center">
                        <input
                          id={`${question.id}-${optionIndex}`}
                          name={question.id}
                          type="radio"
                          value={option}
                          checked={responses[question.id] === option}
                          onChange={(e) => handleInputChange(question.id, e.target.value)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label
                          htmlFor={`${question.id}-${optionIndex}`}
                          className="ml-3 block text-sm text-gray-700"
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                
                {question.type === 'checkbox' && question.options && Array.isArray(question.options) && (
                  <div className="space-y-2">
                    {question.options.map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center">
                        <input
                          id={`${question.id}-${optionIndex}`}
                          type="checkbox"
                          value={option}
                          checked={Array.isArray(responses[question.id]) ? (responses[question.id] as string[]).includes(option) : false}
                          onChange={(e) => handleCheckboxChange(question.id, option, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`${question.id}-${optionIndex}`}
                          className="ml-3 block text-sm text-gray-700"
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                
                {question.type === 'select' && question.options && Array.isArray(question.options) && (
                  <select
                    value={responses[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    className={`block w-full py-2 px-3 border ${
                      errors[question.id] 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    } bg-white rounded-md shadow-sm focus:outline-none sm:text-sm`}
                  >
                    <option value="">Select an option</option>
                    {question.options.map((option, optionIndex) => (
                      <option key={optionIndex} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
                
                {question.type === 'rating' && (
                  <RatingInput
                    questionId={question.id}
                    value={Array.isArray(responses[question.id]) ? '' : String(responses[question.id] || '')}
                    onChange={handleInputChange}
                  />
                )}
                
                {errors[question.id] && (
                  <p className="mt-1 text-sm text-red-600">{errors[question.id]}</p>
                )}
              </div>
            ))}

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader size={16} className="animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Feedback
                    <ArrowRight size={16} className="ml-2" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            Powered by <span className="font-semibold">QR Feedback</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackFormPage;